/**
 * QuickBooksConnect — admin-only setup component.
 *
 * Shows the connection status and lets Greg / Shannon kick off the OAuth flow
 * once. The actual OAuth redirect goes to Intuit, then back to the
 * `quickbooks-oauth-callback` edge function which writes the refresh token
 * + realm ID into Supabase Vault.
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 8, §11)
 */

import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { supabase, SUPABASE_URL } from "../../lib/supabase";

// Intuit OAuth config — client_id is public (it's exposed at every redirect)
// so reading from env is fine. The secret stays in Vault.
const QB_CLIENT_ID = import.meta.env.VITE_QB_CLIENT_ID || "";
const QB_ENVIRONMENT = (import.meta.env.VITE_QB_ENVIRONMENT || "production") as "sandbox" | "production";
const QB_AUTHORIZE_URL = "https://appcenter.intuit.com/connect/oauth2";
const REDIRECT_URI = `${SUPABASE_URL}/functions/v1/quickbooks-oauth-callback`;

export default function QuickBooksConnect() {
  const { isSuperUser } = useAuth();
  const [status, setStatus] = useState<{ kind: "info" | "success" | "error"; msg: string } | null>(null);

  useEffect(() => {
    // Read OAuth result from URL on mount
    const params = new URLSearchParams(window.location.search);
    const result = params.get("qb_oauth");
    if (result === "success") {
      setStatus({ kind: "success", msg: "✅ QuickBooks connected. Refresh token stored in Vault. New invoices will sync automatically." });
    } else if (result === "error") {
      const detail = params.get("detail") ?? "unknown error";
      const friendly =
        detail === "invalid_state"
          ? "OAuth security check failed (state mismatch or expired). Please click Connect again — the link can only be used once and expires after 15 minutes."
          : `⚠ OAuth failed: ${detail}`;
      setStatus({ kind: "error", msg: friendly });
    }
  }, []);

  if (!isSuperUser) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        QuickBooks connection requires super-user access (Greg / Jennifer / Chad / Jeremy).
      </div>
    );
  }

  async function startOAuth() {
    if (!QB_CLIENT_ID) {
      alert("VITE_QB_CLIENT_ID env var not set. Add it to .env.local and rebuild.");
      return;
    }
    setStatus({ kind: "info", msg: "Preparing secure handshake…" });

    // Mint the CSRF state nonce server-side via the qb_oauth_create_state RPC.
    // The callback edge function verifies (and consumes) this nonce BEFORE
    // doing the token exchange, so a forged callback never lands tokens in
    // vault. Falls back to a client-side UUID if the RPC errors — the callback
    // will reject it cleanly, but at least we don't break the flow.
    let state: string;
    const { data: rpcState, error: rpcErr } = await supabase.rpc("qb_oauth_create_state");
    if (rpcErr || !rpcState) {
      setStatus({
        kind: "error",
        msg: `Could not start OAuth: ${rpcErr?.message ?? "qb_oauth_create_state returned empty"}. Make sure you are signed in.`,
      });
      return;
    }
    state = rpcState as string;

    const authUrl =
      `${QB_AUTHORIZE_URL}?client_id=${encodeURIComponent(QB_CLIENT_ID)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent("com.intuit.quickbooks.accounting")}` +
      `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
      `&state=${encodeURIComponent(state)}`;
    window.location.href = authUrl;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-2xl">
      <h2 className="text-lg font-semibold text-bardin-ink mb-1">QuickBooks Online</h2>
      <p className="text-xs text-slate-500 mb-4">
        Connect your QBO company so Bardin's invoices, payments, and contractor Bills sync automatically.
        Environment: <strong>{QB_ENVIRONMENT}</strong>
      </p>

      {status && (
        <div className={
          "rounded-lg px-3 py-2 text-xs mb-3 " +
          (status.kind === "success" ? "bg-emerald-50 text-emerald-700" :
           status.kind === "error"   ? "bg-red-50 text-red-700" :
           "bg-blue-50 text-blue-700")
        }>
          {status.msg}
        </div>
      )}

      <div className="space-y-3 text-sm text-slate-700">
        <p>This is a one-time setup. After connecting, the integration auto-refreshes its access token (Intuit rotates refresh tokens on each refresh; we persist back to Vault).</p>

        <ol className="list-decimal list-inside space-y-1 text-xs text-slate-600 bg-slate-50 rounded-lg p-3">
          <li>In Supabase Vault, set <code className="bg-white px-1 rounded">quickbooks_client_id</code> + <code className="bg-white px-1 rounded">quickbooks_client_secret</code> from your Intuit Developer app.</li>
          <li>In QBO, under <em>Sales → Products and Services</em>, create the 10 standard Items (see migration file 04 for the exact list).</li>
          <li>Click "Connect to QuickBooks" below. You'll be redirected to Intuit, grant access to your Bardin company, and come back here.</li>
          <li>Test by triggering a new invoice (sign a homeowner agreement).</li>
        </ol>

        <button
          onClick={startOAuth}
          className="px-5 py-2 rounded-lg bg-[#2ca01c] text-white text-sm font-semibold hover:opacity-90"
        >
          Connect to QuickBooks
        </button>

        <div className="text-xs text-slate-500 pt-3 border-t border-slate-100">
          <strong>Need help with this integration?</strong>{" "}
          Email{" "}
          <a href="mailto:greg@bardinroofing.com" className="text-bardin-orange hover:underline">
            greg@bardinroofing.com
          </a>{" "}
          or call <a href="tel:+14708238816" className="text-bardin-orange hover:underline">(470) 823-8816</a>.
          Bardin staff can also message Shannon (<a href="mailto:sser@bardinroofing.com" className="text-bardin-orange hover:underline">sser@bardinroofing.com</a>) for finance-side QBO questions.
        </div>
      </div>
    </div>
  );
}
